// The app's session in the page (ADR 0021/0022). WHO is signed in is the identity provider's business:
// its browser flow (the app backend's /auth/*) keeps the provider's session in an HttpOnly cookie. The
// page trades that for the app's own short-lived bearer at `POST /__app/token` (memory only, never
// localStorage), refreshes it before it expires, and sends it to the app's backend, its SDKs' clients,
// the chat service and the app runtime. No password is ever handled here.
import type { AppInfo } from '@cortex/api-kit/app-host';

declare global {
  interface Window {
    /** Written into the page by the app's backend (@cortex/app-host). */
    __CORTEX_APP__?: AppInfo;
  }
}

/** What the app's backend wrote into the page. */
export function appInfo(): AppInfo {
  const i = typeof window !== 'undefined' ? window.__CORTEX_APP__ : undefined;
  if (!i) throw new Error('window.__CORTEX_APP__ is missing: the page is not served by the app’s backend');
  return i;
}

/** Whether the app mounts the SDK of a module. */
export const moduleRuns = (module: string) => appInfo().sdks.some((s) => s.module === module);

export interface AppCaller {
  id: string;
  subject: string;
  email: string;
  name: string | null;
  roles: string[];
}

export type TokenAnswer =
  | { status: 'signed-in'; caller: AppCaller }
  | { status: 'signed-out' }
  /** Signed in at the provider, but the app gives this person no role. */
  | { status: 'refused'; message: string }
  | { status: 'unreachable'; message: string };

let token: string | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<(t: string | undefined) => void>();

/** The app's current bearer (the SDK clients, the chat service and the runtime send it). */
export const getAccessToken = () => token;

/** Told whenever the bearer changes (the runtime's session cookie follows it). */
export function onAccessToken(fn: (t: string | undefined) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const set = (t: string | undefined) => {
  token = t;
  for (const l of listeners) l(t);
};

/** Trades the provider's session for the app's bearer; refreshes it at 80% of its life. */
export async function fetchAppToken(): Promise<TokenAnswer> {
  clearTimeout(timer);
  let res: Response;
  try {
    res = await fetch('/__app/token', { method: 'POST', credentials: 'include' });
  } catch {
    return {
      status: 'unreachable',
      message: `${appInfo().branding.name} could not be reached. Try again in a minute.`,
    };
  }
  const body = (await res.json().catch(() => ({}))) as {
    signedIn?: boolean;
    accessToken?: string;
    expiresIn?: number;
    caller?: AppCaller;
    message?: string;
  };
  if (res.status === 401 || (res.ok && body.signedIn === false)) {
    set(undefined);
    return { status: 'signed-out' };
  }
  if (res.status === 403) {
    set(undefined);
    return { status: 'refused', message: body.message ?? 'You have no access to this app.' };
  }
  if (!res.ok || !body.accessToken || !body.caller) {
    set(undefined);
    return {
      status: 'unreachable',
      message: `${appInfo().branding.name} answered ${res.status}. Try again in a minute.`,
    };
  }
  set(body.accessToken);
  timer = setTimeout(() => void fetchAppToken(), Math.max(5, (body.expiresIn ?? 900) * 0.8) * 1000);
  return { status: 'signed-in', caller: body.caller };
}

/** Forgets the bearer and ends the provider's session too (back to the app's signed-out page). */
export function signOut(): void {
  clearTimeout(timer);
  set(undefined);
  const s = appInfo().signIn;
  window.location.assign(s ? s.signOut : '/login?signed_out=1');
}

/** fetch() on the app's own origin with the app's bearer. */
export function appFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (token && !headers.has('authorization')) headers.set('authorization', `Bearer ${token}`);
  return fetch(path, { ...init, headers });
}

/** GET a JSON answer of the app's backend (null: not found / not served). */
export async function appJson<T>(path: string): Promise<T | null> {
  const r = await appFetch(path);
  if (!r.ok) return null;
  return (await r.json()) as T;
}

/**
 * Signs the page in to the app runtime (its session cookie, which the app's frame carries): `POST
 * /__session` with the bearer. The runtime is this origin.
 */
export async function runtimeSession(): Promise<void> {
  const res = await appFetch('/__session', { method: 'POST', credentials: 'include' });
  if (res.status !== 204) throw new Error(`The app runtime refused the session (${res.status})`);
}

/** Keeps the runtime's session on the current bearer while an app is open. Answers the stop function. */
export function keepRuntimeSession(): () => void {
  return onAccessToken((t) => {
    if (t) void runtimeSession().catch(() => undefined);
  });
}

/** What every SDK client of the page is made with: the app's own origin and its bearer. */
export const sdkClientOptions = () => ({
  baseUrl: window.location.origin,
  token: async () => getAccessToken() ?? null,
});
